import { Input, Select } from '@trackman/web-shared-components';
import Switch from 'components/Switch/Switch';
import { ReactComponent as LockIcon } from 'components/icons/lockIcon.svg';
import { ReactComponent as UnlockIcon } from 'components/icons/unlockIcon.svg';
import { SelectWrapper } from 'components/ui/SelectWrapper/SelectWrapper';
import { ServiceTypes } from 'constants/ServiceTypes';
import { SessionType } from 'enums/SessionType';
import { IField, ILeague, ILevel, IOwningOrganization, IVenue } from 'interfaces/review/session/Session';
import { IApiSessionMetadataUpdated } from 'interfaces/tagging/ApiModels/IApiSessionMetadataUpdated';
import { IOrganization } from 'interfaces/team/IApiScheduleResponse';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useInjection } from 'services/ioc/ioc.react';
import { ContextualDataStore } from 'stores/ContextualDataStore';
import { TeamStore } from 'stores/TeamStore';
import { ISearchTeam, TeamSelector } from '../TeamSelector/TeamSelector';
import {
  EditInputsContainer,
  EditTeams,
  EditWrapper,
  SmallTitle,
  StyledSwitchIcon,
  TeamsContainer,
  TeamsDivider,
  TeamSelectContainer,
  TeamSelectorWrapper,
  TeamTitleWrapper,
  TeamWrapper,
} from './TeamsAndSettingsStyles';

interface IProps {
  session?: IApiSessionMetadataUpdated;
  visible: boolean;
  selectedLeague?: ILeague;
  selectedLevel?: ILevel;
  selectedHomeTeam?: ISearchTeam;
  setSelectedHomeTeam: React.Dispatch<React.SetStateAction<ISearchTeam | undefined>>;
  selectedAwayTeam?: ISearchTeam;
  setSelectedAwayTeam: React.Dispatch<React.SetStateAction<ISearchTeam | undefined>>;
  selectedOrg?: IOwningOrganization;
  setSelectedOrg: React.Dispatch<React.SetStateAction<IOwningOrganization | undefined>>;
  externalSessionId?: string;
  setExternalSessionId: React.Dispatch<React.SetStateAction<string | undefined>>;
  invalidExternalId: boolean;
  setInvalidExternalId: React.Dispatch<React.SetStateAction<boolean>>;
}

const TeamsAndSettings: React.FC<IProps> = observer((props) => {
  const {
    session,
    visible,
    selectedLeague,
    selectedLevel,
    selectedHomeTeam,
    setSelectedHomeTeam,
    selectedAwayTeam,
    setSelectedAwayTeam,
    selectedOrg,
    setSelectedOrg,
    externalSessionId,
    setExternalSessionId,
    invalidExternalId,
    setInvalidExternalId,
  } = props;
  const teamStore = useInjection<TeamStore>(ServiceTypes.TeamStore);
  const [isEditTeamsLocked, setIsEditTeamsLocked] = useState<boolean>(true);
  const contextualDataStore = useInjection<ContextualDataStore>(ServiceTypes.ContextualDataStore);
  const isBp = session?.sessionType === SessionType.BattingPractice;

  const contextualDataToDropdownMap = (obj: ILeague | ILevel | IOwningOrganization | IOrganization | IField | IVenue) => {
    return {
      label: obj?.name,
      value: obj?.id,
    };
  };

  const unlockTeamEdit = async () => {
    if (teamStore.teams.length === 0) {
      await teamStore.fetchTeams();
    }
    setIsEditTeamsLocked((prevState) => !prevState);
  };

  const switchTeams = () => {
    const awayTeamCpy = selectedAwayTeam ? { ...selectedAwayTeam } : undefined;
    setSelectedAwayTeam(selectedHomeTeam);
    setSelectedHomeTeam(awayTeamCpy);
  };

  useEffect(() => {
    if (!isEditTeamsLocked && !teamStore.isLoading && teamStore.teams.length === 0) {
      teamStore.fetchTeams();
    }
  }, [isEditTeamsLocked]);

  useEffect(() => {
    if (
      session &&
      contextualDataStore.owningOrganizations.length > 0 &&
      session.owningOrganizationId &&
      contextualDataStore.owningOrganizations.some((org) => org.id === session.owningOrganizationId)
    ) {
      setSelectedOrg(contextualDataStore.owningOrganizations.find((x) => x.id === session.owningOrganizationId));
    }
  }, [session, contextualDataStore.owningOrganizations]);

  const [homeTeamInputValue, setHomeTeamInputValue] = useState<string>('');
  const [awayTeamInputValue, setAwayTeamInputValue] = useState<string>('');

  return (
    <>
      {!session || !visible ? null : (
        <TeamsContainer>
          <SmallTitle margin='26px 0 7px 0'>
            <EditWrapper>
              <TeamTitleWrapper>
                <div>Teams</div>
                <TeamsDivider />
                <EditTeams>
                  <Switch
                    checked={!isEditTeamsLocked}
                    reversed
                    onClick={unlockTeamEdit}
                    leftContent={<LockIcon />}
                    rightContent={<UnlockIcon />}
                  />
                </EditTeams>
              </TeamTitleWrapper>
            </EditWrapper>
          </SmallTitle>

          <TeamSelectContainer>
            <TeamWrapper>
              <TeamSelectorWrapper disabled>
                <TeamSelector
                  label='Away Team'
                  onSelectionChange={(selectedTeam: any) => setSelectedAwayTeam(selectedTeam)}
                  isDisabled
                  selectedTeam={selectedAwayTeam}
                  inputValue={awayTeamInputValue}
                  setInputValue={setAwayTeamInputValue}
                  isError={!selectedAwayTeam && !isBp}
                />
              </TeamSelectorWrapper>
              <Input
                label='External event team ID'
                type='text'
                value={session.awayTeam?.externalId}
                onChange={() => {}}
                disabled
                width={'416px'}
                margin={'5px 0px'}
              />
            </TeamWrapper>
            <StyledSwitchIcon
              onClick={() => !isEditTeamsLocked && !isBp && switchTeams()}
              disabled={teamStore.isLoading || isEditTeamsLocked || isBp}
            />
            <TeamWrapper>
              <TeamSelectorWrapper disabled>
                <TeamSelector
                  label='Home Team'
                  onSelectionChange={(selectedTeam: any) => {
                    setSelectedHomeTeam(selectedTeam);
                  }}
                  isDisabled
                  selectedTeam={selectedHomeTeam}
                  inputValue={homeTeamInputValue}
                  setInputValue={setHomeTeamInputValue}
                  isError={!selectedHomeTeam && !isBp}
                />
              </TeamSelectorWrapper>
              <Input
                label='External event team ID'
                type='text'
                value={session.homeTeam?.externalId}
                onChange={() => {}}
                disabled
                width={'416px'}
                margin={'5px 0px'}
              />
            </TeamWrapper>
          </TeamSelectContainer>
          <SmallTitle margin='30px 0 16px 0'>Settings</SmallTitle>
          <EditInputsContainer>
            <SelectWrapper>
              <Select
                label='Level'
                selectOptions={(selectedLevel ? [selectedLevel] : []).map(contextualDataToDropdownMap)}
                value={(selectedLevel && contextualDataToDropdownMap(selectedLevel).value) || ''}
                onChange={() => {}}
                width='288px'
                error={!isBp && !selectedLevel}
                margin={'0 0 16px 0'}
                disabled
              />
            </SelectWrapper>
            <SelectWrapper>
              <Select
                label='League'
                selectOptions={(selectedLeague ? [selectedLeague] : []).map(contextualDataToDropdownMap)}
                value={(selectedLeague && contextualDataToDropdownMap(selectedLeague).value) || ''}
                onChange={() => {}}
                width='288px'
                error={!isBp && !selectedLeague}
                margin={'0 0 16px 0'}
                disabled
              />
            </SelectWrapper>
            <SelectWrapper>
              <Select
                label='Owning organization'
                selectOptions={contextualDataStore.owningOrganizations.map(contextualDataToDropdownMap)}
                value={(selectedOrg && contextualDataToDropdownMap(selectedOrg).value) || ''}
                onChange={(event: any) => {
                  setSelectedOrg(contextualDataStore.owningOrganizations.find((x) => x.id === event.target.value));
                }}
                width='288px'
              />
            </SelectWrapper>
          </EditInputsContainer>
          <SmallTitle margin='30px 0 16px 0'>Game reference settings</SmallTitle>
          <EditInputsContainer>
            <Input
              label='Game reference'
              type='text'
              value={session.gameReference || ''}
              onChange={() => {}}
              disabled
              width={'288px'}
              margin={'5px 0px'}
            />
            <Input
              label='External ID'
              type='text'
              value={externalSessionId || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                setExternalSessionId(e.target.value);
                setInvalidExternalId(false);
              }}
              width={'288px'}
              margin={'5px 0px'}
              required
              error={invalidExternalId}
            />
          </EditInputsContainer>
        </TeamsContainer>
      )}
    </>
  );
});

export default TeamsAndSettings;
